/**
 * Chartmind Learning Service
 *
 * Captures "add another" events (add diagnosis, add test, add treatment, add medication)
 * for shadow-mode learning. Each event is stored with context snapshot and location of origin
 * for later review by Professional Reviewers and for suggesting in similar situations.
 *
 * @see Plan: Chartmind Add-Another Learning System
 */

import { db } from './firebase';

const LEARNING_SCHEMA_VERSION = 1;
const TRANSCRIPT_SUMMARY_MAX_LENGTH = 500;

/**
 * Build a context snapshot for similarity and review UI.
 * Keeps PHI minimal (e.g. truncated transcript).
 *
 * @param {string} step - One of 'ddx' | 'diagnostic_plan' | 'treatment' | 'discharge' | 'feedback'
 * @param {Object} state - Current chartmind state from parent
 * @param {string} [state.transcript] - Full or partial transcript
 * @param {string} [state.chiefComplaint] - Chief complaint
 * @param {number} [state.chartmindStep] - Current step index (0-6) when step === 'feedback'
 * @param {Object} [state.ddxData] - DDX visualization data
 * @param {Array} [state.customDiagnoses] - User-added diagnoses
 * @param {Array} [state.tests] - Diagnostic tests
 * @param {Array} [state.treatments] - Treatments
 * @param {Object|string} [state.selectedDiagnosis] - Confirmed diagnosis
 * @param {Array} [state.dischargeMedications] - Current discharge meds (names only for context)
 * @returns {Object} Context snapshot for the learning record
 */
export function buildContextSnapshot(step, state = {}) {
  const transcriptSummary =
    typeof state.transcript === 'string'
      ? state.transcript.slice(0, TRANSCRIPT_SUMMARY_MAX_LENGTH)
      : '';

  const base = {
    chiefComplaint: state.chiefComplaint || '',
    transcriptSummary,
  };

  if (step === 'ddx') {
    const diagnosisNames = [
      ...(state.ddxData?.diagnoses || []).map((d) => d.condition || d.name),
      ...(state.customDiagnoses || []).map((d) => d.name),
    ].filter(Boolean);
    return { ...base, diagnosisNames };
  }

  if (step === 'diagnostic_plan') {
    const diagnosisNames = [
      ...(state.ddxData?.diagnoses || []).map((d) => d.condition || d.name),
      ...(state.customDiagnoses || []).map((d) => d.name),
    ].filter(Boolean);
    const testNames = (state.tests || []).map((t) => t.name).filter(Boolean);
    return { ...base, diagnosisNames, testNames };
  }

  if (step === 'treatment') {
    const selectedDiagnosisName =
      state.selectedDiagnosis &&
      (typeof state.selectedDiagnosis === 'string'
        ? state.selectedDiagnosis
        : state.selectedDiagnosis.condition || state.selectedDiagnosis.name);
    const treatmentNames = (state.treatments || []).map((t) => t.name).filter(Boolean);
    return { ...base, selectedDiagnosisName: selectedDiagnosisName || '', treatmentNames };
  }

  if (step === 'discharge') {
    const dischargeMedicationNames = (state.dischargeMedications || []).map((m) =>
      typeof m === 'string' ? m : m.name
    ).filter(Boolean);
    return { ...base, dischargeMedicationNames };
  }

  if (step === 'feedback') {
    return { ...base, chartmindStep: state.chartmindStep };
  }

  return base;
}

/**
 * Location of origin for the learning (whenever possible).
 * @typedef {Object} LearningOrigin
 * @property {string} [clinicId]
 * @property {string} [clinicName]
 * @property {string} [departmentId]
 * @property {string} [departmentName]
 * @property {string} [regionId]
 * @property {string} [regionName]
 * @property {string} [facilityId]
 */

/**
 * Capture an "add another" event and persist it for review.
 * Writes to clinics/{clinicId}/chartmindLearnings when clinicId is present,
 * otherwise to users/{userId}/chartmindLearnings.
 *
 * @param {Object} params
 * @param {string} params.step - 'ddx' | 'diagnostic_plan' | 'treatment' | 'discharge'
 * @param {Object} params.contextSnapshot - From buildContextSnapshot(step, state)
 * @param {Object} params.addedItem - Step-specific item (e.g. { name, likelihood } for ddx)
 * @param {string} params.userId - Current user id
 * @param {LearningOrigin} [params.origin] - Location of origin (clinicId, departmentId, regionId, etc.)
 * @param {string} [params.promptSource] - Prompt/query source for provenance
 * @param {string} [params.queryId] - Query id for provenance
 * @returns {Promise<string>} Learning document id
 */
export async function captureAddAnother({
  step,
  contextSnapshot,
  addedItem,
  userId,
  origin = {},
  promptSource = null,
  queryId = null,
}) {
  if (!userId) {
    console.warn('[chartmindLearningService] captureAddAnother: userId required');
    return null;
  }

  const now = new Date();
  const id = `learning_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  const doc = {
    id,
    step,
    contextSnapshot: contextSnapshot || {},
    addedItem: addedItem || {},
    userId,
    origin: {
      clinicId: origin.clinicId || null,
      clinicName: origin.clinicName || null,
      departmentId: origin.departmentId || null,
      departmentName: origin.departmentName || null,
      regionId: origin.regionId || null,
      regionName: origin.regionName || null,
      facilityId: origin.facilityId || null,
    },
    learningSchemaVersion: LEARNING_SCHEMA_VERSION,
    promptSource: promptSource || null,
    queryId: queryId || null,
    status: 'pending_review',
    approvedForSimilar: false,
    reviewedBy: null,
    reviewedAt: null,
    scope: null,
    createdAt: now,
    updatedAt: now,
  };

  // Remove undefined so Firestore doesn't throw
  const clean = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) return obj.map(clean).filter((x) => x !== undefined);
    if (typeof obj === 'object') {
      const out = {};
      for (const [k, v] of Object.entries(obj)) {
        if (v !== undefined) out[k] = clean(v);
      }
      return out;
    }
    return obj;
  };
  const cleaned = clean(doc);

  try {
    if (origin.clinicId) {
      await db
        .collection('clinics')
        .doc(origin.clinicId)
        .collection('chartmindLearnings')
        .doc(id)
        .set(cleaned);
    } else {
      await db
        .collection('users')
        .doc(userId)
        .collection('chartmindLearnings')
        .doc(id)
        .set(cleaned);
    }
    return id;
  } catch (err) {
    console.error('[chartmindLearningService] captureAddAnother error:', err);
    return null;
  }
}

/**
 * List learnings for review UI. Queries one source: clinic subcollection or user subcollection.
 *
 * @param {Object} params
 * @param {'clinic'|'user'} params.source - 'clinic' or 'user'
 * @param {string} [params.clinicId] - Required when source === 'clinic'
 * @param {string} [params.userId] - Required when source === 'user'
 * @param {string} [params.step] - Filter by step (ddx, diagnostic_plan, treatment, discharge)
 * @param {string} [params.status] - Filter by status (e.g. pending_review, approved, rejected)
 * @param {number} [params.limit] - Max docs (default 50)
 * @returns {Promise<Array<{ id: string, _source: 'clinic'|'user', _clinicId?: string, _userId?: string } & Record<string,unknown>>>}
 */
export async function listLearningsForReview({
  source,
  clinicId = null,
  userId = null,
  step = null,
  status = null,
  limit = 50,
}) {
  if (source === 'clinic' && !clinicId) {
    console.warn('[chartmindLearningService] listLearningsForReview: clinicId required when source is clinic');
    return [];
  }
  if (source === 'user' && !userId) {
    console.warn('[chartmindLearningService] listLearningsForReview: userId required when source is user');
    return [];
  }

  try {
    const coll =
      source === 'clinic'
        ? db.collection('clinics').doc(clinicId).collection('chartmindLearnings')
        : db.collection('users').doc(userId).collection('chartmindLearnings');

    let query = coll.orderBy('createdAt', 'desc').limit(limit);
    if (step) query = query.where('step', '==', step);
    if (status) query = query.where('status', '==', status);

    const snap = await query.get();
    const out = [];
    snap.docs.forEach((d) => {
      out.push({
        id: d.id,
        _source: source,
        _clinicId: source === 'clinic' ? clinicId : undefined,
        _userId: source === 'user' ? userId : undefined,
        ...d.data(),
      });
    });
    return out;
  } catch (err) {
    console.error('[chartmindLearningService] listLearningsForReview error:', err);
    return [];
  }
}

/**
 * Update a learning's review outcome (Add / Don't add). Caller must have review permission.
 *
 * @param {Object} params
 * @param {'clinic'|'user'} params.source
 * @param {string} [params.clinicId] - Required when source === 'clinic'
 * @param {string} [params.userId] - Required when source === 'user'
 * @param {string} params.learningId
 * @param {boolean} params.approvedForSimilar - true = Add, false = Don't add
 * @param {string} [params.scope] - Optional: 'clinic' | 'region' | 'global'
 * @param {string} params.reviewedBy - UID of reviewer
 * @returns {Promise<boolean>}
 */
export async function setLearningReview({
  source,
  clinicId = null,
  userId = null,
  learningId,
  approvedForSimilar,
  scope = null,
  reviewedBy,
}) {
  if (source === 'clinic' && !clinicId) return false;
  if (source === 'user' && !userId) return false;
  if (!learningId || reviewedBy == null) return false;

  const ref =
    source === 'clinic'
      ? db.collection('clinics').doc(clinicId).collection('chartmindLearnings').doc(learningId)
      : db.collection('users').doc(userId).collection('chartmindLearnings').doc(learningId);

  const now = new Date();
  const update = {
    status: approvedForSimilar ? 'approved' : 'rejected',
    approvedForSimilar: !!approvedForSimilar,
    reviewedBy,
    reviewedAt: now,
    updatedAt: now,
  };
  if (scope != null) update.scope = scope;

  try {
    await ref.update(update);
    return true;
  } catch (err) {
    console.error('[chartmindLearningService] setLearningReview error:', err);
    return false;
  }
}
